import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Key } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/api';
import { flag } from '../../config';

const AdminProfile = () => {
  const [admin, setAdmin] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ email: '', full_name: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await adminApi.get(`/admin/me`);
      setAdmin(response.data);
      setEditData({ email: response.data.email, full_name: response.data.full_name });
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Phase 1 (ADR-0009 PD-27): the email half of this form is hidden unless
      // CREDENTIAL_CHANGE is on, so never send an address the admin cannot see.
      const payload = flag('CREDENTIAL_CHANGE')
        ? editData
        : { full_name: editData.full_name };
      await adminApi.put(`/admin/profile`, payload);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setEditing(false);
      fetchProfile();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await adminApi.post(`/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  if (!admin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#6F5BEB' }}></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-display text-4xl font-bold mb-2" style={{ color: '#111827' }}>
            My Profile
          </h1>
          <p className="font-body" style={{ color: '#475569' }}>
            Manage your account settings and preferences
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
            <p className="font-body" style={{ color: message.type === 'success' ? '#059669' : '#DC2626' }}>
              {message.text}
            </p>
          </div>
        )}

        {/* Profile Info Card */}
        <Card className="border-0 shadow-lg" style={{ borderRadius: '16px' }}>
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold" style={{ color: '#111827' }}>
                Profile Information
              </h2>
              {!editing && (
                <Button
                  onClick={() => setEditing(true)}
                  className="rounded-lg"
                  style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
                >
                  Edit Profile
                </Button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 font-body" style={{ color: '#111827' }}>
                    Full Name
                  </label>
                  <Input
                    type="text"
                    value={editData.full_name}
                    onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                {flag('CREDENTIAL_CHANGE') && (
                  <div>
                    <label htmlFor="admin-email" className="block text-sm font-semibold mb-2 font-body" style={{ color: '#111827' }}>
                      Email
                    </label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-lg"
                    style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setEditData({ email: admin.email, full_name: admin.full_name });
                    }}
                    variant="outline"
                    className="rounded-lg"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User size={20} style={{ color: '#6F5BEB' }} />
                  <div>
                    <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>Full Name</p>
                    <p className="font-semibold font-body" style={{ color: '#111827' }}>{admin.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={20} style={{ color: '#6F5BEB' }} />
                  <div>
                    <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>Email</p>
                    <p className="font-semibold font-body" style={{ color: '#111827' }}>{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield size={20} style={{ color: '#6F5BEB' }} />
                  <div>
                    <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>Username</p>
                    <p className="font-semibold font-body" style={{ color: '#111827' }}>{admin.username}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password Card — hidden in Phase 1 (ADR-0009 PD-27) */}
        <Card className="border-0 shadow-lg" style={{ borderRadius: '16px' }}>
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Key size={24} style={{ color: '#6F5BEB' }} aria-hidden="true" />
              <h2 className="font-display text-2xl font-bold" style={{ color: '#111827' }}>
                Password
              </h2>
            </div>

            {!flag('CREDENTIAL_CHANGE') ? (
              <p className="font-body" style={{ color: '#475569' }}>
                Credential changes are handled by the platform owner.
              </p>
            ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2 font-body" style={{ color: '#111827' }}>
                  Current Password
                </label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 font-body" style={{ color: '#111827' }}>
                  New Password
                </label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 font-body" style={{ color: '#111827' }}>
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-lg"
                style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminProfile;
